import {
  DEFAULT_LOCALE,
  DIRECTION,
  LOCALES,
  translate,
  type Locale,
  type TranslationKey,
  type TranslationParams,
} from "@planner/core";
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from "react";
import { platform } from "../platform/index.js";

/**
 * اللغة — **الطريق الوحيد إلى أي نصّ يراه المستخدم**.
 *
 * `t` لا تقبل إلا مفتاحاً من القاموس، فنصٌّ مكتوب في شاشة لا يمكن أن يمرّ من
 * هنا. وقاعدة lint تمنع كتابة الحروف العربية في ملفات الواجهة أصلاً — القاعدتان
 * معاً تجعلان «شاشة بالعربي فقط» حالةً غير قابلة للحدوث، لا عادةً نتذكّرها.
 */

/** **ويُصدَّر** — فدالّةُ نصٍّ تُمرَّر إلى مساعدٍ تُكتَب بنوعٍ واحد لا بنسخةٍ في كل ملفّ */
export type Translator = (key: TranslationKey, params?: TranslationParams) => string;

type LocaleContextValue = {
  readonly locale: Locale;
  readonly direction: "rtl" | "ltr";
  readonly t: Translator;
  readonly setLocale: (next: Locale) => void;
};

const LocaleContext = createContext<LocaleContextValue | null>(null);
const STORED_LOCALE = "planner.locale";

function initialLocale(): Locale {
  const stored = platform.readPreference(STORED_LOCALE);
  return LOCALES.find((l) => l === stored) ?? DEFAULT_LOCALE;
}

export function LocaleProvider({
  children,
  locale: forced,
}: {
  readonly children: ReactNode;
  /** يُمرَّر في الاختبار وحده، فلا يعتمد الاختبار على تخزين الجهاز */
  readonly locale?: Locale;
}): ReactNode {
  const [locale, setStateLocale] = useState<Locale>(forced ?? initialLocale);

  const setLocale = useCallback((next: Locale) => {
    setStateLocale(next);
    platform.writePreference(STORED_LOCALE, next);
    const root = platform.documentElement;
    if (root) {
      root.lang = next;
      root.dir = DIRECTION[next];
    }
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      direction: DIRECTION[locale],
      t: (key, params) => translate(locale, key, params),
      setLocale,
    }),
    [locale, setLocale],
  );

  return <LocaleContext value={value}>{children}</LocaleContext>;
}

export function useLocale(): LocaleContextValue {
  const value = use(LocaleContext);
  if (!value) throw new Error("useLocale used outside LocaleProvider");
  return value;
}

/** اختصارٌ لأكثر ما يُستعمل — نصّ بمفتاحه */
export function useT(): Translator {
  return useLocale().t;
}
